/** Link PCP office contract. Keep the LinkPortal shared/linkPcpOffice.ts copy in sync. */
export interface PcpOfficeSelection {
  pcpId: string;
  slot: 'PRIMARY' | 'OFFICE_2' | 'OFFICE_3';
  address: string;
  phone: string;
  fax: string;
}

export function pcpReferenceId(value: unknown): string {
  if (value && typeof value === 'object') return String((value as Record<string, unknown>).id || '').trim();
  return typeof value === 'string' ? value.trim() : '';
}

export function pcpOffices(pcpId: string, contact: Record<string, unknown> | null | undefined): PcpOfficeSelection[] {
  if (!contact || !pcpId) return [];
  const data = { ...(contact.data as Record<string, unknown> | undefined), ...contact };
  const text = (field: string) => String(data[field] ?? '').trim();
  const street = [text('street'), text('aptSuite')].filter(Boolean).join(' ');
  const locality = [text('city'), text('state'), text('zipCode')].filter(Boolean).join(', ');
  return [
    { pcpId, slot: 'PRIMARY' as const, address: [street, locality].filter(Boolean).join('\n'), phone: text('phone'), fax: text('fax') },
    { pcpId, slot: 'OFFICE_2' as const, address: text('doctorAddress2'), phone: text('doctorPhone2'), fax: text('doctorFax2') },
    { pcpId, slot: 'OFFICE_3' as const, address: text('doctorAddress3'), phone: text('doctorPhone3'), fax: text('doctorFax3') },
  ].filter(office => office.address || office.phone || office.fax);
}

export function parsePcpOffice(value: unknown): PcpOfficeSelection | null {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    const p = parsed as Record<string, unknown>;
    if (!['PRIMARY', 'OFFICE_2', 'OFFICE_3'].includes(String(p.slot)) || !pcpReferenceId(p.pcpId)) return null;
    if (![p.address, p.phone, p.fax].every(v => typeof v === 'string')) return null;
    return { pcpId: pcpReferenceId(p.pcpId), slot: p.slot as PcpOfficeSelection['slot'], address: p.address as string, phone: p.phone as string, fax: p.fax as string };
  } catch { return null; }
}

/** A selection is the doctor's office slot; its saved address/phone/fax may be outdated. */
export function samePcpOffice(a: PcpOfficeSelection | null, b: PcpOfficeSelection | null): boolean {
  return Boolean(a && b && a.pcpId === b.pcpId && a.slot === b.slot);
}

/** A new assessment inherits the patient preference, never a previous assessment's office. */
export function pcpOfficeAssignment(patient: Record<string, unknown>): Record<string, string> {
  const pcp = pcpReferenceId(patient.pcpId);
  const saved = parsePcpOffice(patient.pcpOfficeSelection);
  const selected = saved?.pcpId === pcp ? saved : null;
  return { pcp, pcpOfficeSelection: selected ? JSON.stringify(selected) : '', pcpSelectedAddress: selected?.address || '' };
}
